import { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

import { http } from '../../features/jobs/api/http';
import { typedLength } from '../../helpers/liveMachineDrums.js';

// Finishing the drum that is on the machine, by hand.
//
// The opposite of cancelling a drum. Cancel says it was never made; this says it
// WAS made and the line never noticed it finishing — the pinoles opened, the
// bobbin came off, and no sensor said so, so the drum stayed open and the queue
// stayed put. On skip2250 that left a drum open across a 31 h stop and the next
// 2 294 m were counted onto it.
//
// It does not complete anything from here. The WORKER owns the active drum: it
// holds the operator spans it has been accumulating, the speed samples, the run
// time, and the profile's counter reset that hands the overshoot on as carry.
// Completing from the browser would write a drum with no operators, no speed and
// no carry, and nothing on screen would say so. So this leaves a request and the
// worker runs the same completion a detected drum end runs — within a loop, a
// second or two, not instantly.
//
// ── and the drum that never ran ───────────────────────────────────────────
//
// The same flag now appears on PENDING entries, and there it means something
// else: a drum that was MADE and never tracked (made on another machine, made
// before the queue was built, made while the line was offline), or one a planner
// is closing out at the length that actually exists.
//
// That one cannot go through the worker — there is no active drum to hand it, no
// operator spans, no run time, and crucially NO COUNTER to default to. So it
// goes straight to the server, the length is REQUIRED, and the three things
// nobody can know are recorded as absent rather than invented.
const fmt2 = v => (v == null ? '-' : Number(v).toFixed(2));

const ForceCompleteModal = ({ show, onHide, target }) => {
  // PENDING (or anything that is not the running drum) takes the second path.
  const running = String(target?.status || 'ACTIVE').toUpperCase() === 'ACTIVE';

  const [length, setLength] = useState('');
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState('');

  useEffect(() => {
    if (!show) return;
    // A drum that never ran has no counter, so there is nothing to leave the box
    // empty FOR. It opens on the planned length — visible, editable, and never 0.
    setLength(running ? '' : target?.planned ? String(target.planned) : '');
    setReason('');
    setPreview(null);
    setErr('');
    setDone('');
  }, [show, target?.tokenId, target?.planned, running]);

  // Asked for on open: the counter is the honest default, and it is the one
  // number the browser cannot work out for itself. Only for the RUNNING drum —
  // the pending path has no counter to ask about.
  useEffect(() => {
    if (!show || !running || !target?.machineId) return;
    let alive = true;
    http
      .post(`/api/redis/queues/${target.machineId}/force-complete`, {
        tokenId: target.tokenId,
        dryRun: true
      })
      .then(({ data }) => {
        if (alive) setPreview(data);
      })
      .catch(e => {
        if (alive) setErr(e?.response?.data?.error || e?.message || 'Could not read the machine.');
      });
    return () => {
      alive = false;
    };
  }, [show, running, target?.machineId, target?.tokenId]);

  const typed = typedLength(length);
  const willBe = Number.isFinite(typed) ? typed : running ? preview?.counter ?? null : null;
  // What the next drum starts from. Positive = handed on, negative = stated past
  // the counter and the surplus is credited to this drum instead. Null when
  // there is nothing to compare: the drum never ran, or no counter was read.
  const carry =
    running && Number.isFinite(Number(preview?.counter)) && Number.isFinite(willBe)
      ? Number(preview.counter) - willBe
      : null;
  // The pending path cannot fall back on anything, so the button waits for a
  // number rather than sending one the server would have to guess at. Zero
  // counts as a number: "close it now at 0 and put the real metres in from the
  // execution log later" is a deliberate move, and only an EMPTY box is refused.
  const needsLength = !running && !Number.isFinite(typed);
  const closingEmpty = !running && Number.isFinite(typed) && typed === 0;

  const submit = async () => {
    setErr('');
    setBusy(true);
    try {
      const { data } = running
        ? await http.post(`/api/redis/queues/${target.machineId}/force-complete`, {
            tokenId: target.tokenId,
            ...(Number.isFinite(typed) ? { length: typed } : {}),
            reason
          })
        : await http.post(`/api/redis/queues/${target.machineId}/complete-pending`, {
            jobId: target.jobId,
            taskId: target.taskId,
            tokenId: target.tokenId,
            length: typed,
            reason
          });
      setDone(data.message || 'Requested.');
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || 'Failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!target) return null;

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title className="fs-8">
          {running ? 'Finish this drum now' : 'Close this drum without running it'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="fs-9 text-700 mb-3">
          <b>{preview?.productLabel || target.productLabel}</b> ·{' '}
          <span className="font-monospace">{target.tokenId}</span> on{' '}
          {String(target.machineId).toUpperCase()}
          <br />
          {running ? (
            preview ? (
              <>
                the counter reads <b>{fmt2(preview.counter)} m</b>, the drum is planned
                for {fmt2(preview.plannedLength)} m
              </>
            ) : (
              <>planned {fmt2(target.planned)} m</>
            )
          ) : (
            <>
              this drum has not started — planned {fmt2(target.planned)} m, counter
              none
            </>
          )}
        </div>

        <Form.Group className="mb-3" style={{ maxWidth: 220 }}>
          <Form.Label className="fs-9 fw-semibold">
            Metres on the drum{running ? '' : ' (required)'}
          </Form.Label>
          <Form.Control
            type="text"
            inputMode="decimal"
            // A placeholder ONLY where an empty box has a meaning. On the pending
            // path there is no counter to fall back on, so the box is prefilled
            // with the plan instead of hinting at a number it would not use.
            placeholder={running && preview?.counter != null ? fmt2(preview.counter) : ''}
            value={length}
            isInvalid={length !== '' && !Number.isFinite(typed)}
            onChange={e => setLength(e.target.value)}
          />
          <Form.Text className="fs-10">
            {running ? (
              <>
                Leave it empty to take the counter. Type a number if what came off
                the machine was measured and differs.
              </>
            ) : (
              <>
                Nothing ran, so there is no counter to take: say how many metres
                are on the drum. <b>0 is allowed</b> — it closes the drum empty so
                the metres can be put in later from the execution log.
              </>
            )}
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label className="fs-9 fw-semibold">
            Why — this goes into the project&apos;s activity log
          </Form.Label>
          <Form.Control
            size="sm"
            value={reason}
            placeholder="e.g. pinoles opened, the drum end was never detected"
            onChange={e => setReason(e.target.value)}
          />
        </Form.Group>

        {running ? (
          <Alert variant="info" className="py-2 fs-10 mb-0">
            The drum closes at <b>{willBe == null ? 'the counter' : `${fmt2(willBe)} m`}</b>,
            its operators and run time are recorded as they stand, anything past that
            length is handed on as carry, and{' '}
            {String(target.machineId).toUpperCase()} moves to the next entry in the
            queue.
            {/* HOW MUCH is handed on. "anything past that length" was true and
                uncheckable: with no figure there was nothing to compare against
                the next drum, so a carry that did move looked like one that had
                not. Reported 2026-10-26 — "pare sa nu imi mute metri in queue". */}
            {carry != null && carry > 0.05 && (
              <div className="mt-1">
                The counter reads <b>{fmt2(preview.counter)} m</b>, so{' '}
                <b>{fmt2(carry)} m</b> go on to the next drum as its starting
                count.
              </div>
            )}
            {carry != null && carry < -0.05 && (
              <div className="mt-1 text-warning">
                That is <b>{fmt2(-carry)} m</b> MORE than the counter has
                ({fmt2(preview.counter)} m). The extra is credited to this drum
                and the next one starts from zero — nothing is carried.
              </div>
            )}
            <div className="mt-1 text-700">
              The line does it, not this screen — so it happens on the machine&apos;s
              next loop, a second or two from now.
            </div>
          </Alert>
        ) : (
          <Alert variant="warning" className="py-2 fs-10 mb-0">
            The drum is recorded as holding{' '}
            <b>{willBe == null ? '—' : `${fmt2(willBe)} m`}</b> and{' '}
            {String(target.machineId).toUpperCase()} will skip it.
            <div className="mt-1">
              <b>No operator, no run time and no speed are recorded</b>, because the
              machine never made it — it will appear in Execution History as a drum
              nobody was at.
            </div>
            {closingEmpty && (
              <div className="mt-1">
                At 0 m the drum is closed EMPTY. It keeps its place, its drum
                record and its execution log, so the real length can be typed in
                from Execution History afterwards.
              </div>
            )}
          </Alert>
        )}

        {err && (
          <Alert variant="danger" className="py-2 fs-9 mt-3 mb-0">
            {err}
          </Alert>
        )}
        {done && (
          <Alert variant="success" className="py-2 fs-9 mt-3 mb-0">
            {done}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="phoenix-secondary" onClick={onHide} disabled={busy}>
          {done ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant="success"
          disabled={
            busy || !!done || (length !== '' && !Number.isFinite(typed)) || needsLength
          }
          title={needsLength ? 'Say how many metres are on the drum' : undefined}
          onClick={submit}
        >
          {busy ? (
            <Spinner animation="border" size="sm" />
          ) : running ? (
            'Finish the drum'
          ) : (
            'Close the drum'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ForceCompleteModal;
